import { tracePeerConnection as tracePC } from './trace';
import obfuscate from './obfuscator';
import { notify } from 'app/services/errorReporter';
import { EntityStringId } from 'app/types/entity';
import { RtcStatsEventType, RtcStatsEvent, IRtcStatsMidsEvent } from 'app/services/rtc/RealtimeProtocolTypes';
import { DistributiveOmit } from 'app/utils/types';

let peerCounter = 0;

interface IOptions {
  getStatsInterval?: number;
}

export type RtcStatsMids = Pick<IRtcStatsMidsEvent, 'localMids' | 'remoteMids'>;

export interface IStatsSender {
  sendPeerMetadata(): Promise<void>;
  sendMids(mids: RtcStatsMids): Promise<void>;
}

export const tracePeerConnection = (
  peerConnection: RTCPeerConnection,
  peerID: EntityStringId,
  send: (payload: DistributiveOmit<RtcStatsEvent, 'timestamp'>) => Promise<void>,
  options: IOptions,
): IStatsSender => {
  const rtcConnId = peerCounter++;

  const sendConnectionEvent = (eventType: string, details: unknown) =>
    send({
      type: RtcStatsEventType.RtcConnectionEvent,
      eventType,
      rtcConnId,
      details,
    });

  const sendPeerMetadata = async () => {
    const metadata = {
      peerID,
    };

    try {
      await sendConnectionEvent('metadata', metadata);
    } catch (error) {
      notify(error, { name: 'rtcstats/init' });
    }
  };

  const sendMids = async (mids: RtcStatsMids) => {
    try {
      await send({
        type: RtcStatsEventType.Mids,
        rtcConnId,
        ...mids,
      });
    } catch (error) {
      notify(error, { name: 'rtcstats/mids' });
    }
  };

  tracePC(peerConnection, {
    ...options,
    trace: async (eventName: string, details: unknown) => {
      try {
        await sendConnectionEvent(eventName, obfuscate(eventName, details));
      } catch (error) {
        notify(error, { name: 'rtcstats/trace' });
      }
    },
  });

  return {
    sendPeerMetadata,
    sendMids,
  };
};
